import { useEffect, useRef, useState } from "react";
import { uploadGoogleImageToGallery } from "../../utils/googleImages";

type BrowserConnection = EventTarget & { downlink?: number; effectiveType?: string; saveData?: boolean };
export type GalleryConnection = { online: boolean; estimatedMbps?: number; slow: boolean };
export type GoogleImageUploadState = {
  phase: "idle" | "sending" | "saving" | "complete" | "error";
  loaded: number;
  total?: number;
  bytesPerSecond?: number;
  startedAt?: number;
  error?: string;
};

const idleState: GoogleImageUploadState = { phase: "idle", loaded: 0 };
const browserConnection = () => typeof navigator === "undefined" ? undefined
  : (navigator as Navigator & { connection?: BrowserConnection }).connection;

function readConnection(): GalleryConnection {
  const connection = browserConnection();
  const speed = connection?.downlink;
  return {
    online: typeof navigator === "undefined" || navigator.onLine !== false,
    estimatedMbps: typeof speed === "number" && speed > 0 ? speed : undefined,
    slow: ["slow-2g", "2g"].includes(connection?.effectiveType || "") ||
      (typeof speed === "number" && speed > 0 && speed < 1),
  };
}

export default function useGoogleImageUpload() {
  const [state, setState] = useState<GoogleImageUploadState>(idleState);
  const [connection, setConnection] = useState(readConnection);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const activeRequest = useRef<AbortController | null>(null);
  const uploading = state.phase === "sending" || state.phase === "saving";

  useEffect(() => {
    const update = () => setConnection(readConnection());
    const network = browserConnection();
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    network?.addEventListener?.("change", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      network?.removeEventListener?.("change", update);
    };
  }, []);

  useEffect(() => {
    if (!uploading || state.startedAt === undefined) return;
    const update = () => setElapsedSeconds(Math.floor((Date.now() - state.startedAt!) / 1000));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [uploading, state.startedAt]);

  useEffect(() => () => activeRequest.current?.abort(), []);

  const upload = async (formData: FormData) => {
    if (activeRequest.current) return null;
    const controller = new AbortController();
    activeRequest.current = controller;
    const startedAt = Date.now();
    setElapsedSeconds(0);
    setState({ phase: "sending", loaded: 0, startedAt });

    try {
      const response = await uploadGoogleImageToGallery(formData, {
        signal: controller.signal,
        onUploadProgress: (event) => {
          if (controller.signal.aborted) return;
          const seconds = (Date.now() - startedAt) / 1000;
          const sent = event.progress === 1 || Boolean(event.total && event.loaded >= event.total);
          setState({
            phase: sent ? "saving" : "sending",
            loaded: event.loaded,
            total: event.total,
            startedAt,
            bytesPerSecond: event.rate || (seconds >= 0.1 ? event.loaded / seconds : undefined),
          });
        },
      });
      if (!controller.signal.aborted) setState((current) => ({ ...current, phase: "complete" }));
      return response;
    } catch (error) {
      if (!controller.signal.aborted) {
        setState((current) => ({
          ...current,
          phase: "error",
          error: error instanceof Error ? error.message : "Could not save this image. Please try again.",
        }));
      }
      throw error;
    } finally {
      if (activeRequest.current === controller) activeRequest.current = null;
    }
  };

  return { state, connection, elapsedSeconds, uploading, upload,
    reset: () => { if (!activeRequest.current) setState(idleState); } };
}
